/**
 * ロール設定タブ — 部署×権限レベルごとの機能アクセスマトリックス。
 *
 * 仕様書: docs/01_spec/spec_user_management.md
 *
 * - 部署タブで切替（管理者 / 営業 / マーケティング / カスタム部署）
 * - 行: FEATURES の大項目 → 小項目（各機能ごとに 4 アクション）
 * - 列: 権限レベル（leader / employee / external）
 * - 各セルは 4 アクションの ON/OFF（ページ優先ルール: view が OFF なら他はグレーアウト）
 * - 「部署を追加」ボタンで新規追加
 * - 「クリーンアップ」ボタンで FEATURES に無い古いキーを一括削除
 */
import {
  ACTION_LABELS,
  FEATURES,
  LEVELS,
  LEVEL_LABELS,
  SYSTEM_DEPARTMENTS,
  type Action,
  type DepartmentId,
  type FeaturePermission,
  type Level,
  type PermissionMap,
} from '@goenhub/shared';
import { Plus, RefreshCw, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useAllRolePermissions,
  useCleanupRolePermissions,
  useDeleteDepartment,
  useDepartments,
  useInitDefaults,
  useUpdateRolePermissions,
  useUpsertDepartment,
} from '@/features/permissions';
import { cn } from '@/lib/utils';

export function RoleSettingsTab() {
  const { data: departments, isLoading: deptsLoading } = useDepartments();
  const { data: rolePerms, isLoading: rpsLoading } = useAllRolePermissions();
  const updateRP = useUpdateRolePermissions();
  const upsertDept = useUpsertDepartment();
  const deleteDept = useDeleteDepartment();
  const cleanup = useCleanupRolePermissions();
  const initDefaults = useInitDefaults();

  const [activeDept, setActiveDept] = useState<DepartmentId | null>(null);
  const [dirtyMap, setDirtyMap] = useState<Record<string, PermissionMap>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [newDeptId, setNewDeptId] = useState('');
  const [newDeptName, setNewDeptName] = useState('');

  const currentDept: DepartmentId | null = useMemo(() => {
    if (activeDept) return activeDept;
    return (departments?.[0]?.id as DepartmentId | undefined) ?? null;
  }, [activeDept, departments]);

  if (deptsLoading || rpsLoading) return <Spinner />;
  if (!departments || departments.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-[var(--text-muted)]">
        部署が登録されていません。「ユーザー」画面の「初期化」ボタンを押してください。
      </div>
    );
  }

  /** 表示用 PermissionMap を返す（dirty 優先、なければ保存済み）。 */
  const getDisplayPermMap = (dept: DepartmentId, level: Level): PermissionMap => {
    const key = `${dept}__${level}`;
    if (dirtyMap[key]) return dirtyMap[key];
    const saved = rolePerms?.find((rp) => rp.id === key);
    return (saved?.permissions ?? {}) as PermissionMap;
  };

  const handleToggle = (dept: DepartmentId, level: Level, featureId: string, action: Action) => {
    const key = `${dept}__${level}`;
    const current = { ...getDisplayPermMap(dept, level) };
    const featurePerm: FeaturePermission = { ...(current[featureId] ?? {}) };
    featurePerm[action] = !featurePerm[action];
    // ページ優先ルール: view を OFF にしたら他のアクションも自動 OFF
    if (action === 'view' && !featurePerm.view) {
      featurePerm.create = false;
      featurePerm.edit = false;
      featurePerm.delete = false;
    }
    current[featureId] = featurePerm;
    setDirtyMap((m) => ({ ...m, [key]: current }));
  };

  const handleSave = async (dept: DepartmentId) => {
    for (const level of LEVELS) {
      const key = `${dept}__${level}`;
      if (!dirtyMap[key]) continue;
      try {
        await updateRP.mutateAsync({ dept, level, permissions: dirtyMap[key] });
      } catch (err) {
        toast.error(
          `${dept}/${level} の保存に失敗: ${err instanceof Error ? err.message : String(err)}`,
        );
        return;
      }
    }
    setDirtyMap((m) => {
      const next = { ...m };
      for (const level of LEVELS) delete next[`${dept}__${level}`];
      return next;
    });
    toast.success(`${dept} の権限設定を保存しました`);
  };

  const handleInitDefaults = async () => {
    if (
      !confirm(
        '既定の部署 (管理 / 営業 / マーケティング) と各ロールの既定権限を生成します。\n既存データは保護され、不足分のみ補完されます。\n実行しますか？',
      )
    )
      return;
    try {
      const result = (await initDefaults.mutateAsync()) as {
        departmentsCreated: number;
        rolePermissionsCreated: number;
      };
      toast.success(
        `初期化完了: 部署 ${result.departmentsCreated} 件、ロール権限 ${result.rolePermissionsCreated} 件作成`,
      );
    } catch (err) {
      toast.error(`初期化に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleReset = (dept: DepartmentId) => {
    setDirtyMap((m) => {
      const next = { ...m };
      for (const level of LEVELS) delete next[`${dept}__${level}`];
      return next;
    });
  };

  const handleAddDept = async () => {
    if (!newDeptId || !newDeptName) return;
    try {
      await upsertDept.mutateAsync({
        id: newDeptId.trim().toLowerCase(),
        name: newDeptName.trim(),
        order: (departments?.length ?? 0) + 10,
      });
      toast.success(`部署「${newDeptName}」を追加しました`);
      setAddOpen(false);
      setNewDeptId('');
      setNewDeptName('');
      setActiveDept(newDeptId.trim().toLowerCase() as DepartmentId);
    } catch (err) {
      toast.error(`追加に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDeleteDept = async (deptId: DepartmentId) => {
    if (!window.confirm(`部署「${deptId}」を削除します。よろしいですか？`)) return;
    try {
      await deleteDept.mutateAsync(deptId);
      toast.success(`部署「${deptId}」を削除しました`);
      if (activeDept === deptId) setActiveDept(null);
    } catch (err) {
      toast.error(`削除に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleCleanup = async () => {
    try {
      const result = (await cleanup.mutateAsync()) as { totalRemoved: number };
      toast.success(`クリーンアップ完了: ${result.totalRemoved} 件削除`);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const currentDeptObj = departments.find((d) => d.id === currentDept);
  // 初期部署: 保存値 (isSystem) か、コードの初期部署一覧 (後から初期部署になった経理も含む)
  const isSystemDept = (d: { id: string; isSystem?: boolean }) =>
    !!d.isSystem || (SYSTEM_DEPARTMENTS as readonly string[]).includes(d.id);
  const isDirty = currentDept ? LEVELS.some((lv) => dirtyMap[`${currentDept}__${lv}`]) : false;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          {departments.map((d) => (
            <button
              type="button"
              key={d.id}
              onClick={() => setActiveDept(d.id as DepartmentId)}
              className={cn(
                'px-3 py-1.5 rounded text-xs border transition-colors',
                currentDept === d.id
                  ? 'bg-[var(--accent-subtle)] border-[var(--accent)] text-[var(--accent)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
              )}
            >
              {d.name}
              {isSystemDept(d) && <span className="ml-1 text-[var(--text-muted)]">(初期)</span>}
            </button>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={() => setAddOpen(true)}>
            <Plus />
            部署追加
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {currentDeptObj && !isSystemDept(currentDeptObj) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteDept(currentDeptObj.id as DepartmentId)}
              title="部署を削除"
            >
              <Trash2 />
              部署削除
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleInitDefaults}
            disabled={initDefaults.isPending}
            title="既定の部署と各ロールの既定権限を生成 (初回 + 不足分の補完)"
          >
            <RefreshCw />
            初期化
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handleCleanup}>
            <RotateCcw />
            クリーンアップ
          </Button>
        </div>
      </div>

      {currentDept && (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-[var(--text-secondary)]">
              チェックを変更したら「保存」を押してください。view を OFF
              にすると他アクションも自動的に無効になります（ページ優先ルール）。
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleReset(currentDept)}
                disabled={!isDirty}
              >
                変更を破棄
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => handleSave(currentDept)}
                disabled={!isDirty || updateRP.isPending}
              >
                <Save />
                {updateRP.isPending ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-[var(--border)] rounded">
              <thead className="bg-[var(--bg-hover)]">
                <tr>
                  <th className="text-left p-2 font-medium sticky left-0 bg-[var(--bg-hover)] z-10 min-w-[260px]">
                    機能
                  </th>
                  {LEVELS.map((lv) => (
                    <th key={lv} className="text-center p-2 font-medium" colSpan={4}>
                      {LEVEL_LABELS[lv]}
                    </th>
                  ))}
                </tr>
                <tr className="text-[10px] text-[var(--text-muted)]">
                  <th className="sticky left-0 bg-[var(--bg-hover)] z-10"></th>
                  {LEVELS.flatMap((lv) =>
                    (['view', 'create', 'edit', 'delete'] as Action[]).map((act) => (
                      <th key={`${lv}-${act}`} className="text-center p-1 font-normal">
                        {ACTION_LABELS[act]}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((group) => {
                  // v3.3: Hub アイテムは権限管理対象外 → タブを展開して leaf featureId のみ列挙
                  // 同一 featureId が複数タブで共有される場合は最初の出現のみ
                  const seen = new Set<string>();
                  const leafItems: (typeof group.items)[number][] = [];
                  for (const it of group.items) {
                    if (it.tabs && it.tabs.length > 0) {
                      for (const tab of it.tabs) {
                        if (tab.id.startsWith('_hub.')) continue;
                        if (seen.has(tab.id)) continue;
                        seen.add(tab.id);
                        leafItems.push(tab);
                      }
                    } else if (!it.id.startsWith('_hub.')) {
                      if (!seen.has(it.id)) {
                        seen.add(it.id);
                        leafItems.push(it);
                      }
                    }
                  }
                  if (leafItems.length === 0) return null;
                  return (
                    <>
                      <tr key={`group-${group.id}`} className="bg-[var(--bg-surface)]">
                        <td
                          colSpan={1 + LEVELS.length * 4}
                          className="p-2 font-semibold text-[var(--text-secondary)] sticky left-0 bg-[var(--bg-surface)]"
                        >
                          {group.emoji ? `${group.emoji} ${group.label}` : group.label}
                        </td>
                      </tr>
                      {leafItems.map((item) => (
                        <tr key={item.id} className="border-t border-[var(--border)]">
                          <td className="p-2 sticky left-0 bg-[var(--bg-surface)] z-10">
                            <div className="font-medium">{item.label}</div>
                            <div className="text-[10px] text-[var(--text-muted)] font-mono">
                              {item.id}
                            </div>
                          </td>
                          {LEVELS.map((lv) => {
                            const perms = getDisplayPermMap(currentDept, lv);
                            const fp = perms[item.id] ?? {};
                            const viewOn = Boolean(fp.view);
                            return (['view', 'create', 'edit', 'delete'] as Action[]).map((act) => {
                              const applicable = item.actions.includes(act);
                              const disabled = !applicable || (act !== 'view' && !viewOn);
                              const checked = Boolean(fp[act]);
                              return (
                                <td key={`${lv}-${act}`} className="text-center p-1">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => handleToggle(currentDept, lv, item.id, act)}
                                    className="size-4 align-middle"
                                  />
                                </td>
                              );
                            });
                          })}
                        </tr>
                      ))}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>部署を追加</DialogTitle>
            <DialogDescription>
              部署 ID は半角英数字+ハイフン（2-32 文字）で指定してください。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="dept-id">部署 ID</Label>
              <Input
                id="dept-id"
                placeholder="creative"
                value={newDeptId}
                onChange={(e) => setNewDeptId(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="dept-name">部署名</Label>
              <Input
                id="dept-name"
                placeholder="クリエイティブ"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleAddDept}
              disabled={upsertDept.isPending || !newDeptId || !newDeptName}
            >
              {upsertDept.isPending ? '追加中...' : '追加'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
